<div class="role24-form-container">
  <p-toast></p-toast>
  <app-sheet-loader [visible]="isLoading" />

  <p-card [header]="formTitle" [subheader]="isDev ? schema.label : ''" *ngIf="!isLoading && schema && form" class="form-card">

    <form [formGroup]="form">
      <!-- Section 1: Disclosure TextBlock (only first section before table) -->
      <ng-container *ngFor="let control of schema.controls; let controlIndex = index; trackBy: trackByQname">
        <!-- Only show the first Level 2 header and TextBlock before the table starts -->
        <ng-container *ngIf="controlIndex < 2">
          <!-- Section Headers (Level 2) - First section only -->
          <div *ngIf="control.type === 'SectionHeader' && control.ui.level === 2" class="bg-gray-200 text-black-700 text-sm font-medium px-2 py-1 mt-3">
            {{ control.label }}
          </div>

          <!-- TextBlock Fields - First section only -->
          <div class="form-field" *ngIf="control.type === 'TextBlock' && shouldDisplayField(control)">
            <label class="field-label" [class.required]="isFieldRequired(control)">
              {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
            </label>

            <div class="field-input-wrapper">
              <app-rich-text-field
                [formControlName]="control.qname"
                [title]="control.label"
                [placeholder]="control | placeholder: 'Click to add content…'"
                [invalid]="!!(getControl(control.qname)?.invalid && getControl(control.qname)?.touched)"
              ></app-rich-text-field>

              <small class="error-message"
                     *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
                <span *ngIf="getControl(control.qname)?.errors?.['required']">
                  {{ control.error?.['required'] || 'This is a mandatory field.' }}
                </span>
              </small>
            </div>
          </div>
        </ng-container>
      </ng-container>

      <!-- Section 2: Consolidated Table (only show if Nature of Statements is Consolidated) -->
      <div class="financial-statement-table-wrapper mt-4 viewport-sticky" *ngIf="showConsolidatedTable">
        <table
          class="financial-statement-table"
          appFieldLinkTarget
          fieldLinkSheet="notes-related-party-transactions"
          [fieldLinkControls]="schema.controls"
          [fieldLinkSections]="consolidatedSectionSet"
        >
          <thead>
            <!-- Row 1: Consolidation type (spans amount columns) + fixed columns -->
            <tr>
              <th class="description-column" rowspan="3">Type of Transaction</th>
              <th class="amount-column merged-header" colspan="8">Consolidated</th>
              <th class="actions-column" rowspan="3">Actions</th>
              <th class="validation-status-column" rowspan="3">Mapping Status</th>
              <th class="validation-error-column" rowspan="3">Error Description</th>
            </tr>
            <!-- Row 2: Financial-period date range -->
            <tr>
              <th class="amount-column merged-header" colspan="8">
                <div>{{ periodHeaderCurrent }}</div>
                <div>(Current)</div>
              </th>
            </tr>
            <!-- Row 3: Individual column headers -->
            <tr>
              <th class="amount-column">Parent</th>
              <th class="amount-column">Entities with joint<br/>control or significant<br/>influence over entity</th>
              <th class="amount-column">Subsidiaries</th>
              <th class="amount-column">Associates</th>
              <th class="amount-column">Joint ventures<br/>where entity<br/>is venturer</th>
              <th class="amount-column">Key management<br/>personnel of entity<br/>or parent</th>
              <th class="amount-column">Other related<br/>parties</th>
              <th class="amount-column total-column">Total</th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let control of schema.controls; let controlIndex = index; trackBy: trackByQname">
              <!-- Level 2 Section Headers (inside table) -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 2 && isInsideTableSection(control)" class="header-level-2">
                <td colspan="12" class="section-header">
                  {{ control.label }}
                </td>
              </tr>

              <!-- Level 3 Section Headers -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 3" class="header-level-3">
                <td colspan="12" class="section-header">
                  {{ control.label }}
                </td>
              </tr>

              <!-- Level 4 Section Headers (with expand/collapse toggle) - Consolidated -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 4" class="header-level-4" (click)="toggleSectionConsolidated(control.qname)">
                <td colspan="12" class="section-header">
                  <i class="pi" [ngClass]="isSectionExpandedConsolidated(control.qname) ? 'pi pi-chevron-down' : 'pi-chevron-right'" style="margin-right: 0.5rem;"></i>
                  {{ control.label }}
                </td>
              </tr>

              <!-- Table Column Fields (transaction rows) - Consolidated -->
              <tr *ngIf="control.ui.tableColumn && shouldShowRowConsolidated(control, controlIndex)"
                  class="data-row"
                  [attr.data-qname]="control.qname"
                  [class.row-has-error]="rowHasBlockingError(control, 'Consolidated')"
                  [class.row-column-sum-error]="hasColumnSumError(control, 'Consolidated', 'Total')">
                <td class="description-column">
                  <app-field-link-label [control]="control" /><app-required-marker [when]="isFieldRequired(control)" />
                </td>

                <!-- Consolidated - Parent -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Consolidated', 'Parent')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Consolidated', 'Parent')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Consolidated', 'Parent') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Consolidated - Entities with joint control or significant influence over entity -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Consolidated', 'JointControl')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Consolidated', 'JointControl')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Consolidated', 'JointControl') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Consolidated - Subsidiaries -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Consolidated', 'Subsidiaries')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Consolidated', 'Subsidiaries')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Consolidated', 'Subsidiaries') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Consolidated - Associates -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Consolidated', 'Associates')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Consolidated', 'Associates')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Consolidated', 'Associates') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Consolidated - Joint ventures where entity is venturer -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Consolidated', 'JointVentures')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Consolidated', 'JointVentures')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Consolidated', 'JointVentures') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Consolidated - Key management personnel of entity or parent -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Consolidated', 'KeyManagement')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Consolidated', 'KeyManagement')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Consolidated', 'KeyManagement') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Consolidated - Other related parties -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Consolidated', 'OtherRelatedParties')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Consolidated', 'OtherRelatedParties')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Consolidated', 'OtherRelatedParties') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Consolidated - Total (User-editable) -->
                <td class="amount-column total-column" [class.cell-invalid]="isCellInvalid(control, 'Consolidated', 'Total')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControl]="$any(form.get(getXbrlControlName(control.qname, 'Consolidated', 'Total')))"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [styleClass]="isInputInvalid(control, 'Consolidated', 'Total') ? 'p-invalid' : ''"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                  ></p-inputnumber>
                </td>

                <!-- Actions Column -->
                <td class="actions-column">
                  <ng-container *ngIf="!control.ui.calculated && !control.ui.readonly">
                    <!-- Pencil Icon (Not in edit mode) -->
                    <svg *ngIf="isValidationComplete && !isRowInEditMode(control, 'Consolidated')"
                         class="action-icon pencil-icon"
                         viewBox="0 0 16 16"
                         fill="none"
                         xmlns="http://www.w3.org/2000/svg"
                         (click)="enterEditMode(control, 'Consolidated')"
                         title="Edit">
                      <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M12.1657 1.94834C12.3339 1.77238 12.5635 1.67334 12.8032 1.67334C13.0429 1.67334 13.2725 1.77238 13.4407 1.94834L14.3824 2.93668C14.5507 3.10668 14.6457 3.34001 14.6457 3.58334C14.6457 3.82668 14.5507 4.06001 14.3824 4.23001L5.03906 13.8633C4.96573 13.94 4.87573 13.9967 4.77573 14.0267L2.10906 14.8933C1.96906 14.9367 1.81906 14.9067 1.70239 14.8133C1.58573 14.72 1.51906 14.5733 1.52573 14.42L1.68906 11.6667C1.69573 11.5633 1.73239 11.4633 1.79239 11.38L11.1357 1.74668C11.4724 1.40001 12.0357 1.40001 12.3724 1.74668L12.1657 1.94834ZM12.8032 2.77334L13.4407 3.42668L12.8032 4.08001L12.1657 3.42668L12.8032 2.77334ZM11.1657 4.42668L4.46906 11.38L4.36906 12.98L5.90906 12.6133L12.6057 5.66334L11.1657 4.42668Z"
                        fill="currentColor" />
                    </svg>

                    <!-- Tick and Cross Icons (In edit mode) -->
                    <div *ngIf="isRowInEditMode(control, 'Consolidated')" class="action-icons-group">
                      <svg class="action-icon tick-icon"
                           viewBox="0 0 16 16"
                           fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                           (click)="saveEditMode(control, 'Consolidated')"
                           title="Save">
                        <path d="M13.5 3.5L6 11L2.5 7.5"
                          stroke="currentColor" stroke-width="2"
                          stroke-linecap="round" stroke-linejoin="round" />
                      </svg>

                      <svg class="action-icon cross-icon"
                           viewBox="0 0 16 16"
                           fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                           (click)="cancelEditMode(control, 'Consolidated')"
                           title="Cancel">
                        <path d="M12 4L4 12M4 4L12 12"
                          stroke="currentColor" stroke-width="2"
                          stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </div>
                  </ng-container>
                </td>

                <!-- Mapping Status -->
                <td
                  class="validation-status-column"
                  [ngClass]="{
                    'status-failed': isValidationComplete && getRowValidationStatus(control, 'Consolidated') === 'Failed',
                    'status-validated-warning': isValidationComplete && getRowValidationStatus(control, 'Consolidated') === 'Validated*'
                  }"
                >
                  <ng-container *ngIf="isValidationComplete; else validatingConsolidated">
                    <ng-container [ngSwitch]="getRowValidationStatus(control, 'Consolidated')">
                      <ng-container *ngSwitchCase="'Validated*'">
                        Validated<span class="status-asterisk">*</span>
                      </ng-container>
                      <ng-container *ngSwitchCase="'Failed'">
                        Failed
                      </ng-container>
                      <ng-container *ngSwitchDefault>
                        {{ getRowValidationStatus(control, 'Consolidated') }}
                      </ng-container>
                    </ng-container>
                  </ng-container>
                  <ng-template #validatingConsolidated>
                    <p-skeleton width="70%" height="1rem" />
                  </ng-template>
                </td>

                <!-- Error Description -->
                <td class="validation-error-column">
                  <ng-container *ngIf="isValidationComplete; else errorLoadingConsolidated">
                    <span [innerHTML]="getRowErrorDescription(control, 'Consolidated')"></span>
                  </ng-container>
                  <ng-template #errorLoadingConsolidated>
                    <p-skeleton width="100%" height="1rem" />
                  </ng-template>
                </td>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>

      <!-- Section 3: Separate Table -->
      <div class="financial-statement-table-wrapper mt-4 viewport-sticky">
        <table
          class="financial-statement-table"
          appFieldLinkTarget
          fieldLinkSheet="notes-related-party-transactions"
          [fieldLinkControls]="schema.controls"
          [fieldLinkSections]="separateSectionSet"
        >
          <thead>
            <!-- Row 1: Consolidation type (spans amount columns) + fixed columns -->
            <tr>
              <th class="description-column" rowspan="3">Type of Transaction</th>
              <th class="amount-column merged-header" colspan="8">Separate</th>
              <th class="actions-column" rowspan="3">Actions</th>
              <th class="validation-status-column" rowspan="3">Mapping Status</th>
              <th class="validation-error-column" rowspan="3">Error Description</th>
            </tr>
            <!-- Row 2: Financial-period date range -->
            <tr>
              <th class="amount-column merged-header" colspan="8">
                <div>{{ periodHeaderCurrent }}</div>
                <div>(Current)</div>
              </th>
            </tr>
            <!-- Row 3: Individual column headers -->
            <tr>
              <th class="amount-column">Parent</th>
              <th class="amount-column">Entities with joint<br/>control or significant<br/>influence over entity</th>
              <th class="amount-column">Subsidiaries</th>
              <th class="amount-column">Associates</th>
              <th class="amount-column">Joint ventures<br/>where entity<br/>is venturer</th>
              <th class="amount-column">Key management<br/>personnel of entity<br/>or parent</th>
              <th class="amount-column">Other related<br/>parties</th>
              <th class="amount-column total-column">Total</th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let control of schema.controls; let controlIndex = index; trackBy: trackByQname">
              <!-- Level 2 Section Headers (inside table) -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 2 && isInsideTableSection(control)" class="header-level-2">
                <td colspan="12" class="section-header">
                  {{ control.label }}
                </td>
              </tr>

              <!-- Level 3 Section Headers -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 3" class="header-level-3">
                <td colspan="12" class="section-header">
                  {{ control.label }}
                </td>
              </tr>

              <!-- Level 4 Section Headers (with expand/collapse toggle) - Separate -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 4" class="header-level-4" (click)="toggleSectionSeparate(control.qname)">
                <td colspan="12" class="section-header">
                  <i class="pi" [ngClass]="isSectionExpandedSeparate(control.qname) ? 'pi-chevron-down' : 'pi-chevron-right'" style="margin-right: 0.5rem;"></i>
                  {{ control.label }}
                </td>
              </tr>

              <!-- Table Column Fields (transaction rows) - Separate -->
              <tr *ngIf="control.ui.tableColumn && shouldShowRowSeparate(control, controlIndex)"
                  class="data-row"
                  [attr.data-qname]="control.qname"
                  [class.row-has-error]="rowHasBlockingError(control, 'Separate')"
                  [class.row-column-sum-error]="hasColumnSumError(control, 'Separate', 'Total')">
                <td class="description-column">
                  <app-field-link-label [control]="control" /><app-required-marker [when]="isFieldRequired(control)" />
                </td>

                <!-- Separate - Parent -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Separate', 'Parent')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Separate', 'Parent')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Separate', 'Parent') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Separate - Entities with joint control or significant influence over entity -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Separate', 'JointControl')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Separate', 'JointControl')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Separate', 'JointControl') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Separate - Subsidiaries -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Separate', 'Subsidiaries')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Separate', 'Subsidiaries')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Separate', 'Subsidiaries') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Separate - Associates -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Separate', 'Associates')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Separate', 'Associates')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Separate', 'Associates') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Separate - Joint ventures where entity is venturer -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Separate', 'JointVentures')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Separate', 'JointVentures')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Separate', 'JointVentures') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Separate - Key management personnel of entity or parent -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Separate', 'KeyManagement')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Separate', 'KeyManagement')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Separate', 'KeyManagement') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Separate - Other related parties -->
                <td class="amount-column" [class.cell-invalid]="isCellInvalid(control, 'Separate', 'OtherRelatedParties')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControlName]="getXbrlControlName(control.qname, 'Separate', 'OtherRelatedParties')"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [styleClass]="isInputInvalid(control, 'Separate', 'OtherRelatedParties') ? 'p-invalid' : ''"
                  ></p-inputnumber>
                </td>

                <!-- Separate - Total (User-editable) -->
                <td class="amount-column total-column" [class.cell-invalid]="isCellInvalid(control, 'Separate', 'Total')">
                  <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                    [formControl]="$any(form.get(getXbrlControlName(control.qname, 'Separate', 'Total')))"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [styleClass]="isInputInvalid(control, 'Separate', 'Total') ? 'p-invalid' : ''"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                  ></p-inputnumber>
                </td>

                <!-- Actions Column -->
                <td class="actions-column">
                  <ng-container *ngIf="!control.ui.calculated && !control.ui.readonly">
                    <!-- Pencil Icon (Not in edit mode) -->
                    <svg *ngIf="isValidationComplete && !isRowInEditMode(control, 'Separate')"
                         class="action-icon pencil-icon"
                         viewBox="0 0 16 16"
                         fill="none"
                         xmlns="http://www.w3.org/2000/svg"
                         (click)="enterEditMode(control, 'Separate')"
                         title="Edit">
                      <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M12.1657 1.94834C12.3339 1.77238 12.5635 1.67334 12.8032 1.67334C13.0429 1.67334 13.2725 1.77238 13.4407 1.94834L14.3824 2.93668C14.5507 3.10668 14.6457 3.34001 14.6457 3.58334C14.6457 3.82668 14.5507 4.06001 14.3824 4.23001L5.03906 13.8633C4.96573 13.94 4.87573 13.9967 4.77573 14.0267L2.10906 14.8933C1.96906 14.9367 1.81906 14.9067 1.70239 14.8133C1.58573 14.72 1.51906 14.5733 1.52573 14.42L1.68906 11.6667C1.69573 11.5633 1.73239 11.4633 1.79239 11.38L11.1357 1.74668C11.4724 1.40001 12.0357 1.40001 12.3724 1.74668L12.1657 1.94834ZM12.8032 2.77334L13.4407 3.42668L12.8032 4.08001L12.1657 3.42668L12.8032 2.77334ZM11.1657 4.42668L4.46906 11.38L4.36906 12.98L5.90906 12.6133L12.6057 5.66334L11.1657 4.42668Z"
                        fill="currentColor" />
                    </svg>

                    <!-- Tick and Cross Icons (In edit mode) -->
                    <div *ngIf="isRowInEditMode(control, 'Separate')" class="action-icons-group">
                      <svg class="action-icon tick-icon"
                           viewBox="0 0 16 16"
                           fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                           (click)="saveEditMode(control, 'Separate')"
                           title="Save">
                        <path d="M13.5 3.5L6 11L2.5 7.5"
                          stroke="currentColor" stroke-width="2"
                          stroke-linecap="round" stroke-linejoin="round" />
                      </svg>

                      <svg class="action-icon cross-icon"
                           viewBox="0 0 16 16"
                           fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                           (click)="cancelEditMode(control, 'Separate')"
                           title="Cancel">
                        <path d="M12 4L4 12M4 4L12 12"
                          stroke="currentColor" stroke-width="2"
                          stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </div>
                  </ng-container>
                </td>

                <!-- Mapping Status -->
                <td
                  class="validation-status-column"
                  [ngClass]="{
                    'status-failed': isValidationComplete && getRowValidationStatus(control, 'Separate') === 'Failed',
                    'status-validated-warning': isValidationComplete && getRowValidationStatus(control, 'Separate') === 'Validated*'
                  }"
                >
                  <ng-container *ngIf="isValidationComplete; else validatingSeparate">
                    <ng-container [ngSwitch]="getRowValidationStatus(control, 'Separate')">
                      <ng-container *ngSwitchCase="'Validated*'">
                        Validated<span class="status-asterisk">*</span>
                      </ng-container>
                      <ng-container *ngSwitchCase="'Failed'">
                        Failed
                      </ng-container>
                      <ng-container *ngSwitchDefault>
                        {{ getRowValidationStatus(control, 'Separate') }}
                      </ng-container>
                    </ng-container>
                  </ng-container>
                  <ng-template #validatingSeparate>
                    <p-skeleton width="70%" height="1rem" />
                  </ng-template>
                </td>

                <!-- Error Description -->
                <td class="validation-error-column">
                  <ng-container *ngIf="isValidationComplete; else errorLoadingSeparate">
                    <span [innerHTML]="getRowErrorDescription(control, 'Separate')"></span>
                  </ng-container>
                  <ng-template #errorLoadingSeparate>
                    <p-skeleton width="100%" height="1rem" />
                  </ng-template>
                </td>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>
      <submit-for-validation
        [form]="form"
        [sheetKey]="'notesRelatedPartyTransactions'"
        [formTitle]="formTitle"
        [evaluateSheetStatus]="evaluateSheetStatus.bind(this)"
        (cancelConfirmed)="onCancelConfirmed()"
      ></submit-for-validation>
    </form>
  </p-card>
</div>
